<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="fixed inset-0 flex items-center justify-center bg-black/65 p-4 backdrop-blur-sm"
      :class="zIndexClass"
      @click.self="emit('close')"
    >
    <section class="flex max-h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-2xl">
      <header class="flex flex-col gap-4 border-b border-zinc-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p class="text-xs font-bold uppercase tracking-[0.12em] text-blue-600">Media Content</p>
          <h2 class="mt-1 text-lg font-bold text-zinc-950">{{ title }}</h2>
          <p class="mt-1 text-xs text-zinc-500">{{ allowUpload ? `Upload a new ${mediaLabel} or reuse one already saved for this creator.` : `Choose an existing ${mediaLabel.toLowerCase()} for this preview.` }}</p>
        </div>
        <div class="flex items-center gap-2">
          <button
            v-if="allowUpload"
            type="button"
            class="inline-flex items-center gap-2 rounded-xl bg-[#2478ff] px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-600 disabled:cursor-wait disabled:opacity-60"
            :disabled="uploading"
            @click="openFilePicker"
          >
            <ArrowUpTrayIcon class="h-4 w-4" />
            {{ uploading ? `Uploading ${uploadProgress}%` : `Upload ${mediaLabel}` }}
          </button>
          <button
            type="button"
            class="rounded-xl border border-zinc-300 p-2.5 text-zinc-500 hover:bg-zinc-50"
            aria-label="Close media picker"
            @click="emit('close')"
          >
            <XMarkIcon class="h-5 w-5" />
          </button>
        </div>
        <input
          ref="fileInput"
          type="file"
          class="hidden"
          :accept="accept"
          @change="uploadSelectedFile"
        />
      </header>

      <div class="grid gap-3 border-b border-zinc-200 bg-zinc-50 px-5 py-3 sm:grid-cols-[240px_1fr]">
        <PlatformaSelect
          v-model="selectedFolderId"
          :options="folderOptions"
          placeholder="Choose a folder"
          @update:model-value="loadFiles"
        />
        <div class="relative">
          <MagnifyingGlassIcon class="pointer-events-none absolute left-3 top-3 h-4 w-4 text-zinc-400" />
          <input
            v-model.trim="search"
            type="search"
            class="block w-full rounded-xl border-zinc-300 py-2.5 pl-9 pr-3 text-sm focus:border-blue-500 focus:ring-blue-500"
            :placeholder="`Search ${mediaLabel.toLowerCase()}s`"
          />
        </div>
      </div>

      <p v-if="error" class="border-b border-red-100 bg-red-50 px-5 py-3 text-sm text-red-700">{{ error }}</p>

      <div class="platforma-media-scroll min-h-0 flex-1 overflow-y-auto p-5">
        <div v-if="loading" class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          <div v-for="row in 8" :key="row" class="aspect-[4/3] animate-pulse rounded-xl bg-zinc-100"></div>
        </div>

        <div v-else-if="!filteredFiles.length" class="flex min-h-64 flex-col items-center justify-center text-center">
          <div class="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50">
            <PhotoIcon v-if="mediaType === 'image'" class="h-7 w-7 text-blue-600" />
            <MusicalNoteIcon v-else-if="mediaType === 'audio'" class="h-7 w-7 text-blue-600" />
            <FilmIcon v-else class="h-7 w-7 text-blue-600" />
          </div>
          <h3 class="mt-4 text-sm font-bold text-zinc-900">No {{ mediaLabel.toLowerCase() }}s found</h3>
          <p class="mt-1 max-w-sm text-xs leading-5 text-zinc-500">{{ allowUpload ? 'Upload one here and it will also remain available in Media Content for future Webinar events.' : 'Add Media Content first, then return here to choose it.' }}</p>
        </div>

        <div v-else class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          <button
            v-for="file in filteredFiles"
            :key="fileKey(file)"
            type="button"
            class="group overflow-hidden rounded-xl border border-zinc-200 bg-white text-left transition hover:border-blue-500 hover:shadow-md"
            @click="selectFile(file)"
          >
            <div class="relative aspect-video overflow-hidden bg-zinc-100">
              <img
                v-if="previewUrl(file)"
                :src="previewUrl(file)"
                :alt="fileName(file)"
                class="h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
              />
              <div v-else class="flex h-full flex-col items-center justify-center gap-2 text-xs font-medium text-zinc-500">
                <MusicalNoteIcon v-if="mediaType === 'audio'" class="h-9 w-9 text-blue-500" />
                <span>{{ mediaType === 'video' ? 'Thumbnail processing' : mediaType === 'audio' ? 'Audio episode' : 'Preview unavailable' }}</span>
              </div>
              <span
                v-if="mediaType === 'video'"
                class="absolute bottom-2 left-2 rounded-full bg-black/75 px-2 py-1 text-[10px] font-semibold text-white"
              >
                {{ normalizedStatus(file) }}
              </span>
            </div>
            <div class="p-3">
              <p class="truncate text-sm font-semibold text-zinc-900">{{ fileName(file) }}</p>
              <p class="mt-1 text-xs text-zinc-500">{{ mediaLabel }} · {{ formatSize(file.size_bytes || file.sizeBytes) }}</p>
            </div>
          </button>
        </div>
      </div>
      </section>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import {
  ArrowUpTrayIcon,
  FilmIcon,
  MagnifyingGlassIcon,
  MusicalNoteIcon,
  PhotoIcon,
  XMarkIcon,
} from '@heroicons/vue/24/outline';
import { useMediaUploadStore } from '@/stores/mediaUploadStore';
import PlatformaSelect from '@/Components/PlatformaSelect.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  mediaType: { type: String, default: 'image' },
  title: { type: String, default: 'Choose media' },
  uploadOrigin: { type: String, default: 'creator-webinar-media' },
  allowUpload: { type: Boolean, default: true },
  zIndexClass: { type: String, default: 'z-[1900]' },
});

const emit = defineEmits(['close', 'select']);
const uploadStore = useMediaUploadStore();
const apiProxyUrl = String(process.env.VUE_APP_API_PROXY_URL || '').replace(/\/$/, '');
const fileInput = ref(null);
const folders = ref([]);
const files = ref([]);
const selectedFolderId = ref('');
const search = ref('');
const loading = ref(false);
const uploading = ref(false);
const uploadProgress = ref(0);
const error = ref('');

const mediaLabel = computed(() => (props.mediaType === 'video' ? 'Video' : props.mediaType === 'audio' ? 'Audio' : 'Image'));
const accept = computed(() => (props.mediaType === 'video' ? 'video/*' : props.mediaType === 'audio' ? 'audio/*' : 'image/*'));
const folderOptions = computed(() => folders.value.map((folder) => ({ value: String(folder.id), label: String(folder.name || 'Untitled folder') })));
const filteredFiles = computed(() => {
  const query = search.value.toLowerCase();
  return files.value.filter((file) => {
    const type = String(file.file_type || file.fileType || '').toLowerCase();
    const mime = String(file.mime_type || file.mimeType || '').toLowerCase();
    const matchesType = type ? type === props.mediaType : mime.startsWith(`${props.mediaType}/`);
    return matchesType && (!query || fileName(file).toLowerCase().includes(query));
  });
});

function fileId(file) {
  return String(file?.file_id || file?.fileId || '');
}

function fileKey(file) {
  return fileId(file) || String(file?.id || fileName(file));
}

function fileName(file) {
  return String(file?.file_name || file?.fileName || 'Untitled media');
}

function normalizedStatus(file) {
  const status = String(file?.status || 'processing').toLowerCase();
  return status === 'ready' ? 'Ready' : status === 'failed' ? 'Failed' : 'Processing';
}

function imageProxyUrl(id) {
  return id ? `${apiProxyUrl}/api/image/${encodeURIComponent(id)}` : '';
}

function videoThumbnailUrl(id) {
  return id ? `${apiProxyUrl}/api/video-thumbnail/${encodeURIComponent(id)}` : '';
}

function previewUrl(file) {
  if (props.mediaType === 'audio') return '';
  if (props.mediaType === 'video') {
    return String(file?.secure_thumbnail_url || file?.secureThumbnailUrl || file?.thumbnail_url || file?.thumbnailUrl || videoThumbnailUrl(fileId(file)) || '');
  }
  return String(file?.secure_image_url || file?.secureImageUrl || imageProxyUrl(fileId(file)) || file?.file_url || file?.fileUrl || '');
}

function formatSize(value) {
  const bytes = Number(value || 0);
  if (!bytes) return 'Size unavailable';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

async function fetchJson(path, options = {}) {
  const response = await fetch(`${apiProxyUrl}${path}`, { credentials: 'include', ...options });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload?.message || payload?.error || `Media request failed (${response.status})`);
  return payload;
}

async function loadFolders() {
  let payload = await fetchJson('/api/list-folders');
  let rows = Array.isArray(payload?.folders) ? payload.folders : [];
  if (!rows.length) {
    await fetchJson('/api/create-folder', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: 'Main', parentId: null }),
    });
    payload = await fetchJson('/api/list-folders');
    rows = Array.isArray(payload?.folders) ? payload.folders : [];
  }
  folders.value = rows;
  if (!rows.some((folder) => String(folder.id) === selectedFolderId.value)) {
    const main = rows.find((folder) => String(folder.name || '').toLowerCase() === 'main' && !folder.parent_id);
    selectedFolderId.value = String(main?.id || rows[0]?.id || '');
  }
}

async function loadFiles() {
  if (!selectedFolderId.value) {
    files.value = [];
    return;
  }
  loading.value = true;
  error.value = '';
  try {
    const payload = await fetchJson(`/api/list-files?folderId=${encodeURIComponent(selectedFolderId.value)}`);
    files.value = Array.isArray(payload?.files) ? payload.files : [];
  } catch (requestError) {
    error.value = String(requestError?.message || 'Unable to load Media Content.');
    files.value = [];
  } finally {
    loading.value = false;
  }
}

async function initialize() {
  loading.value = true;
  error.value = '';
  search.value = '';
  try {
    await loadFolders();
    await loadFiles();
  } catch (requestError) {
    error.value = String(requestError?.message || 'Unable to open Media Content.');
  } finally {
    loading.value = false;
  }
}

function openFilePicker() {
  if (uploading.value) return;
  fileInput.value?.click();
}

async function uploadSelectedFile(event) {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file) return;
  if (!String(file.type || '').toLowerCase().startsWith(`${props.mediaType}/`)) {
    error.value = `Choose a valid ${mediaLabel.value.toLowerCase()} file.`;
    return;
  }

  uploading.value = true;
  uploadProgress.value = 0;
  error.value = '';
  try {
    const uploaded = await uploadStore.uploadSingleFile(file, selectedFolderId.value || null, {
      origin: props.uploadOrigin,
      onProgress: ({ progress }) => { uploadProgress.value = Number(progress || 0); },
    });
    if (!uploaded?.fileId) throw new Error('The media upload did not finish.');
    emit('select', {
      fileId: String(uploaded.fileId),
      fileName: file.name,
      fileUrl: String(uploaded.permanentUrl || ''),
      previewUrl: props.mediaType === 'video'
        ? videoThumbnailUrl(uploaded.fileId)
        : props.mediaType === 'image' ? imageProxyUrl(uploaded.fileId) : '',
      hlsPlaylistUrl: String(uploaded.hlsPlaylistUrl || ''),
      status: uploaded.hlsStatus || 'ready',
      mediaType: props.mediaType,
    });
    emit('close');
  } catch (uploadError) {
    error.value = String(uploadError?.message || `Unable to upload this ${mediaLabel.value.toLowerCase()}.`);
  } finally {
    uploading.value = false;
  }
}

function selectFile(file) {
  const id = fileId(file);
  emit('select', {
    fileId: id,
    fileName: fileName(file),
    fileUrl: String(file?.file_url || file?.fileUrl || ''),
    previewUrl: previewUrl(file),
    hlsPlaylistUrl: props.mediaType === 'video'
      ? `${apiProxyUrl}/api/stream-hls/playlist/${encodeURIComponent(id)}`
      : '',
    status: String(file?.status || ''),
    mediaType: props.mediaType,
  });
  emit('close');
}

watch(() => props.show, (open) => {
  if (open) void initialize();
});
</script>

<style scoped>
.platforma-media-scroll {
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
}

.platforma-media-scroll:hover {
  scrollbar-color: rgba(100, 116, 139, 0.58) transparent;
}

.platforma-media-scroll::-webkit-scrollbar {
  width: 6px;
}

.platforma-media-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.platforma-media-scroll::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: transparent;
}

.platforma-media-scroll:hover::-webkit-scrollbar-thumb {
  background: rgba(100, 116, 139, 0.58);
}

.platforma-media-scroll::-webkit-scrollbar-button {
  display: none;
  width: 0;
  height: 0;
}
</style>
